// src/components/reports/OutcomeNetwork.jsx
//
// Force-directed network of outcome measure co-occurrences.
// Ported from takso-poc/takso-reporting-ui/src/charts/components/OutcomeNetwork.jsx.
//
// Counting unit is the ACTIVITY — what it actually had measured, via ANY of
// its evaluation plans combined — not the activity's objectives (sObjective,
// which only records what was intended), and not the individual plan either:
// an activity that splits two outcomes across two separate single-outcome
// plans still measured two outcomes for that activity, and those two
// outcomes still co-occurred there. Grouping by plan would wrongly count
// that as two unrelated single measurements instead of one dual activity.
// Scoped to completed (Archived) single activities only (backend query).
//
// Two distinct node populations:
//   Connected nodes  — outcomes measured alongside a second outcome
//                      somewhere on the same activity ("dual" activities).
//                      Sized by paired_count: how many dual activities
//                      included this outcome. Gravitate to centre.
//   Isolated nodes   — outcomes that were the ONLY outcome ever measured on
//                      an activity ("single" activities). Sized by
//                      solo_count, scaled to the same max as connected
//                      nodes. Cluster top-right. Cannot form connections.
//   The same outcome can appear in BOTH zones simultaneously — some of its
//   activities were dual, others single. These are distinct statements.
//   Link weight (link_count) — how many dual activities measured that
//   specific pair of outcomes together. Sum of all link_counts equals the
//   stats line's "dual outcomes" activity count.
//
// Interaction:
//   • Drag nodes to reposition them. Once dropped, a dragged node is pinned
//     in place — excluded from all physics — until the chart's underlying
//     data reloads (new activity/org selection, filter change, etc.), so
//     manual layout adjustments survive the simulation settling further.
//   • Click a node once to select it (first of a pair); click a second node to
//     reveal their connection (link highlighted amber).
//   • Click a link count label to highlight that connection; click the same
//     (already-highlighted) label again to open the outcome-pair analysis
//     page in a new tab (see OutcomeRelationship.jsx).
//   • Click anywhere else to clear the selection.

import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { colorFor } from '../../theme/palette';
import { OUTCOME_ICONS } from '../../lib/outcomeIcons';

const ICON = OUTCOME_ICONS;

// sOutcome has no short-label column (checked: title/description/graded/
// gradedDescription/preamble only) — titles range from two words to
// sentence-length ("Positive sense of place in the local built and natural
// environment enhanced"), so persistent labels wrap the full title onto up
// to two lines rather than truncating it.
function wrapTitle(title, maxLen = 26) {
  const t = title || '';
  if (t.length <= maxLen) return [t];
  const words = t.split(' ');
  let line1 = '',
    line2 = '';
  words.forEach((w) => {
    if (!line2 && (line1 ? `${line1} ${w}` : w).length <= maxLen) {
      line1 = line1 ? `${line1} ${w}` : w;
    } else {
      line2 = line2 ? `${line2} ${w}` : w;
    }
  });
  return line2 ? [line1, line2] : [line1];
}

// Single-line label for the always-on "Show node names" view — prefers
// sOutcome.shortLabel (see migration 20260711000001), falling back to a
// truncated title for any outcome that doesn't have one set yet.
function nodeLabelText(node, maxLen = 24) {
  if (node.shortLabel) return node.shortLabel;
  const t = node.title || '';
  return t.length > maxLen ? `${t.slice(0, maxLen - 1).trimEnd()}…` : t;
}

// Half the rendered label box width (mirrors the boxW formula used when
// actually drawing it) — used by simStep to give labelled nodes extra
// separation so neighbouring labels don't overlap.
function labelHalfWidth(node) {
  return (nodeLabelText(node).length * 5.6 + 10) / 2;
}

// Radius factory.
// maxUsage = max(all paired_counts of connected nodes).
// Both connected and isolated nodes scale against this same reference.
function makeRadius(nodes, width, height) {
  const maxUsage = Math.max(
    1,
    ...nodes.map((n) => (n.paired_count > 0 ? n.paired_count : n.solo_count))
  );
  const MIN_RATIO = 0.25;
  const canvasArea = width * height;

  let maxR = Math.max(18, width * 0.05);
  while (maxR > 14) {
    const minR = maxR * MIN_RATIO;
    // testFn is created and fully consumed within this same iteration (via
    // the reduce() call directly below) before maxR is mutated again — it
    // never escapes the loop.
    // eslint-disable-next-line no-loop-func
    const testFn = (n) => {
      const c = n.paired_count > 0 ? n.paired_count : n.solo_count;
      return minR + (c / maxUsage) ** 0.55 * (maxR - minR);
    };
    const fill =
      nodes.reduce((s, n) => s + Math.PI * testFn(n) ** 2, 0) / canvasArea;
    if (fill <= 0.5) break;
    maxR *= 0.93;
  }

  const minR = maxR * MIN_RATIO;
  return (n) => {
    const c = n.paired_count > 0 ? n.paired_count : n.solo_count;
    return minR + (c / maxUsage) ** 0.55 * (maxR - minR);
  };
}

// Direct label-collision correction — a deterministic constraint pass, not
// a force. The gravity/repulsion tuning above nudges nodes toward
// reasonable label spacing, but it's fighting several other forces (link
// attraction, radial gravity, cohesion) for the same node positions, and
// for a crowded cluster with long labels the tug-of-war can settle short of
// actually clearing an overlap no matter how it's tuned. This runs after
// the physics position update each frame: for any two nodes whose label
// boxes still overlap vertically and horizontally, push them directly
// apart along x by the overlap amount. Cheap (label boxes are just rects at
// a fixed y-offset below each node) and self-correcting every frame, so it
// converges to zero label overlap and then just holds steady.
function resolveLabelOverlaps(nodes, radius, width, pinnedId, pinnedIds) {
  const boxes = nodes.map((n) => {
    const bw = labelHalfWidth(n) * 2;
    const r = radius(n);
    const by = n.is_isolated ? n.y + r + 21 : n.y + r + 4;
    return { n, bw, by, bh: 14 };
  });
  // Chains of 3+ nodes whose labels all crowd the same neighbourhood (e.g.
  // a middle node overlapping both neighbours) don't settle in a single
  // pairwise sweep — fixing one pair can partially re-open the other, since
  // the shared node moves for both. Relaxing over several passes within the
  // same frame converges those chains properly instead of leaving it to
  // fight the other forces back and forth across frames indefinitely.
  for (let pass = 0; pass < 6; pass++) {
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i],
          b = boxes[j];
        const vOverlap = a.by < b.by + b.bh && b.by < a.by + a.bh;
        if (!vOverlap) continue;
        const dx = b.n.x - a.n.x;
        const minSep = (a.bw + b.bw) / 2 + 4;
        const dist = Math.abs(dx) || 0.01;
        if (dist >= minSep) continue;
        const push = (minSep - dist) / 2;
        const dir = dx >= 0 ? 1 : -1;
        const aFixed = a.n.id === pinnedId || pinnedIds?.has(a.n.id);
        const bFixed = b.n.id === pinnedId || pinnedIds?.has(b.n.id);
        if (!aFixed) a.n.x -= dir * push * (bFixed ? 2 : 1);
        if (!bFixed) b.n.x += dir * push * (aFixed ? 2 : 1);
      }
    }
  }
  // Clamp by label half-width, not node radius — the render's own boxX
  // clamp (see the always-on label JSX) uses the label box, so if this used
  // the (much smaller) node radius instead, a node could keep drifting
  // past where its label fits, "winning" separation in x that the render
  // then collapses straight back by clamping the box to the edge. That's
  // exactly why this pass could show a fully-resolved distance one moment
  // and identical-looking overlap on screen the next — the position and
  // the rendered box were being clamped to two different boundaries.
  nodes.forEach((n) => {
    const half = labelHalfWidth(n);
    n.x = Math.max(half + 2, Math.min(width - half - 2, n.x));
  });
}

// Simulation tick.
// Connected nodes gravitate to centre; isolated nodes to top-right quadrant.
// pinnedId — node currently being actively dragged (velocity zeroed so it
//   doesn't fight the mouse; position is set directly by the caller).
// pinnedIds — Set of node ids the user has manually dropped after a drag.
//   These are permanently excluded from position/velocity updates (forces
//   still accumulate into fx/fy for consistency but are discarded) until
//   the chart's underlying data reloads and the set is cleared.
function simStep(
  nodes,
  links,
  width,
  height,
  radius,
  maxLink,
  alpha,
  pinnedId,
  pinnedIds,
  showLabels
) {
  nodes.forEach((n) => {
    n.fx = 0;
    n.fy = 0;
  });

  // Repulsion between all nodes.
  // Isolated-to-isolated repulsion is reduced so they can cluster naturally.
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i],
        b = nodes[j];
      const bothIsolated = a.is_isolated && b.is_isolated;
      // Isolated-to-isolated repulsion is normally kept low so they cluster
      // naturally, but that directly fights label spacing — raise it while
      // labels are shown so the cluster is willing to loosen up for them.
      const repK = bothIsolated ? (showLabels ? 9000 : 4000) : 18000;
      const dx = b.x - a.x,
        dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
      const rSum = radius(a) + radius(b);
      // Extra breathing room between connected nodes now that the largest
      // ones converge on the centre — otherwise the big circles crowd
      // together with barely a gap. When labels are shown, widen it further
      // by each node's rendered label half-width so neighbouring labels
      // don't overlap — this only strengthens the "push" term below once
      // nodes are already close enough for their labels to actually be at
      // risk, so it doesn't inflate spacing for pairs that are far apart.
      const gap =
        (bothIsolated ? 8 : 22) +
        (showLabels ? labelHalfWidth(a) + labelHalfWidth(b) : 0);
      const f = (repK / (dist * dist)) * alpha;
      const overlap = rSum + gap - dist;
      // Alpha-independent floor while labels are shown: every other force
      // in this function is scaled by the same decaying alpha, so once it
      // settles near zero nothing moves — including two nodes still stuck
      // mid-overlap because the label-driven gap needed more transient
      // force than they happened to get before freezing. Keeping a small
      // minimum push alive (only when there's still an actual overlap to
      // resolve) guarantees labelled layouts keep separating until clear,
      // however long that takes, rather than settling into whatever the
      // one-off reheat's random path happened to land on.
      const pushAlpha = showLabels ? Math.max(alpha, 0.05) : alpha;
      const push = overlap > 0 ? (overlap / rSum) * 12 * pushAlpha : 0;
      const fx = (dx / dist) * (f + push),
        fy = (dy / dist) * (f + push);
      a.fx -= fx;
      a.fy -= fy;
      b.fx += fx;
      b.fy += fy;
    }
  }

  // Link attraction (connected nodes only — links reference positive IDs).
  // Kept deliberately weaker than the radial-by-size gravity below: its job
  // is now just to nudge linked nodes toward the same angular neighbourhood,
  // not to override how far from centre a node sits (that's size's job).
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  (links || []).forEach(({ outcome1, outcome2, link_count }) => {
    const s = nodeMap[outcome1],
      t = nodeMap[outcome2];
    if (!s || !t) return;
    const dx = t.x - s.x,
      dy = t.y - s.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const norm = link_count / maxLink;
    const idealDist = radius(s) + radius(t) + 55 + (1 - norm) * 380;
    const force = (dist - idealDist) * (0.012 * norm + 0.002) * alpha;
    const fx = (dx / dist) * force,
      fy = (dy / dist) * force;
    s.fx += fx;
    s.fy += fy;
    t.fx -= fx;
    t.fy -= fy;
  });

  // Gravity: connected nodes are pulled toward a target RADIUS from centre
  // based on their size — largest → target radius ~0 (centre), smallest →
  // the outer ring — while keeping whichever angle they're already at.
  // Size decides distance from centre; repulsion decides angular spacing
  // (and keeps same-size nodes from overlapping at the centre); links only
  // give a gentle nudge toward their partner's angular neighbourhood.
  // Isolated → upper-right cluster, unchanged.
  const connectedRadii = nodes.filter((n) => !n.is_isolated).map(radius);
  const minCR = connectedRadii.length ? Math.min(...connectedRadii) : 0;
  const maxCR = connectedRadii.length ? Math.max(...connectedRadii) : 1;
  const crSpan = Math.max(1, maxCR - minCR);
  const cx = width / 2,
    cy = height / 2;
  const outerR = Math.min(width, height) * 0.42;

  // Eased off while labels are shown, same reasoning as the cohesion below:
  // every isolated node pulled toward one exact point would recompress the
  // cluster over time regardless of how hard repulsion pushes back.
  const isolatedTargetK = showLabels ? 0.0012 : 0.003;
  // A single row across a ~40%-width quadrant wasn't nearly enough space
  // for several long labels (5 isolated nodes here needed ~555px of row
  // width against a ~270px budget) — repulsion just lost that fight
  // forever. Lay them out like wrapped text instead: pack left-to-right
  // along the top, right-anchored per row (to still read as "top-right"
  // when everything fits on one line), wrapping to additional rows below
  // whenever a row would run past the canvas width.
  const isolatedList = nodes.filter((n) => n.is_isolated);
  if (showLabels && isolatedList.length) {
    const margin = 20;
    const avail = Math.max(80, width - margin * 2);
    const rows = [[]];
    let curW = 0;
    isolatedList.forEach((n) => {
      const need = labelHalfWidth(n) * 2 + 16;
      if (curW + need > avail && rows[rows.length - 1].length > 0) {
        rows.push([]);
        curW = 0;
      }
      rows[rows.length - 1].push({ n, need });
      curW += need;
    });
    rows.forEach((row, ri) => {
      const rowTotal = row.reduce((s, r) => s + r.need, 0);
      let x = Math.max(margin, width - margin - rowTotal);
      const ty = height * 0.12 + ri * 34;
      row.forEach(({ n, need }) => {
        n.__isoTargetX = x + need / 2;
        n.__isoTargetY = ty;
        x += need;
      });
    });
  }
  nodes.forEach((n) => {
    if (n.is_isolated) {
      const tx =
        showLabels && n.__isoTargetX != null ? n.__isoTargetX : width * 0.8;
      const ty =
        showLabels && n.__isoTargetY != null ? n.__isoTargetY : height * 0.25;
      n.fx += (tx - n.x) * isolatedTargetK * alpha;
      n.fy += (ty - n.y) * isolatedTargetK * alpha;
      return;
    }
    const sizeNorm = (radius(n) - minCR) / crSpan; // 0 = smallest, 1 = largest
    const targetR = (1 - sizeNorm) * outerR;
    const dx = n.x - cx,
      dy = n.y - cy;
    const curR = Math.sqrt(dx * dx + dy * dy) || 0.01;
    const ux = dx / curR,
      uy = dy / curR;
    const diff = targetR - curR;
    const k = 0.02;
    n.fx += ux * diff * k * alpha;
    n.fy += uy * diff * k * alpha;
  });

  // Strong centroid cohesion — pulls isolated nodes into an organic cluster.
  // Eased off while labels are shown so the boosted repulsion above can
  // actually win and spread the cluster out enough to read.
  const isolatedGroup = nodes.filter((n) => n.is_isolated);
  if (isolatedGroup.length > 1) {
    const cx =
      isolatedGroup.reduce((s, n) => s + n.x, 0) / isolatedGroup.length;
    const cy =
      isolatedGroup.reduce((s, n) => s + n.y, 0) / isolatedGroup.length;
    const cohesionK = showLabels ? 0.015 : 0.03;
    isolatedGroup.forEach((n) => {
      n.fx += (cx - n.x) * cohesionK * alpha;
      n.fy += (cy - n.y) * cohesionK * alpha;
    });
  }

  nodes.forEach((n) => {
    if (n.id === pinnedId || pinnedIds?.has(n.id)) {
      n.vx = 0;
      n.vy = 0;
      return;
    }
    n.vx = (n.vx + n.fx) * 0.6;
    n.vy = (n.vy + n.fy) * 0.6;
    const r = radius(n);
    // Clamp x by label half-width (not just node radius) while labels are
    // shown, matching resolveLabelOverlaps and the render's own boxX clamp
    // below — otherwise this clamp lets a node drift past where its label
    // actually fits, and the label-collision pass spends every frame
    // fighting a position this loop keeps dragging back toward the edge.
    const xMargin = showLabels ? Math.max(r + 2, labelHalfWidth(n) + 2) : r + 2;
    n.x = Math.max(xMargin, Math.min(width - xMargin, n.x + n.vx));
    n.y = Math.max(r + 2, Math.min(height - r - 2, n.y + n.vy));
  });

  if (showLabels) {
    resolveLabelOverlaps(nodes, radius, width, pinnedId, pinnedIds);
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function OutcomeNetwork({
  outcomes,
  links,
  pairedCounts = [],
  soloCounts = [],
  planCount = 0,
  dualActivityCount = 0,
  singleActivityCount = 0,
  showLinkCounts = true,
  showNodeLabels = false,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(800);
  const initH =
    typeof window !== 'undefined' ? Math.min(550, window.innerHeight) : 550;
  const [height, setHeight] = useState(initH);
  const heightRef = useRef(initH);
  const [renderNodes, setRenderNodes] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    heightRef.current = height;
  }, [height]);

  const nodesRef = useRef(null);
  const radiusRef = useRef(null);
  const maxLinkRef = useRef(1);
  const alphaRef = useRef(0);
  const rafRef = useRef(null);
  const dragRef = useRef(null);
  const dragMovedRef = useRef(false);
  // Node ids the user has manually dropped after dragging — excluded from
  // all physics until the chart's data reloads (see init effect below).
  const pinnedIdsRef = useRef(new Set());
  // Read inside the RAF loop via ref, not the prop directly — the loop is
  // set up once in the init effect below (keyed on the data, not this
  // prop), so a plain closure over showNodeLabels would go stale the moment
  // the toggle changes without the data itself changing.
  const showNodeLabelsRef = useRef(showNodeLabels);
  useEffect(() => {
    showNodeLabelsRef.current = showNodeLabels;
    // Reheat to a full restart, not just a nudge — every force in simStep
    // is scaled by alpha, so once it's decayed near zero nothing moves
    // regardless of how large the new label-driven gap requirement is.
    // Needs the full transient window to actually resolve, not just enough
    // alpha to twitch before re-freezing in the same overlapping spot.
    alphaRef.current = 1.0;
  }, [showNodeLabels]);

  // Width + height observers
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) =>
      setWidth(e.contentRect.width || 800)
    );
    obs.observe(el);
    setWidth(el.getBoundingClientRect().width || 800);

    const measureHeight = () => {
      const rect = el.getBoundingClientRect();
      // Buffer trimmed from 56 to 20 — the ChartHost controls row (pptx
      // badge) and this component's own stats line now sit above the SVG
      // and already push rect.top down; the old fixed buffer on top of that
      // was over-shrinking the simulation's canvas (isolated nodes crowding
      // toward the top-right corner instead of having proper room).
      const available = window.innerHeight - rect.top - 20;
      if (available > 100) setHeight(Math.min(550, Math.max(380, available)));
    };
    measureHeight();
    window.addEventListener('resize', measureHeight);
    window.addEventListener('scroll', measureHeight, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measureHeight);
      window.removeEventListener('scroll', measureHeight);
    };
  }, []);

  // Initialise + RAF loop
  useEffect(() => {
    if (!outcomes?.length) return;
    cancelAnimationFrame(rafRef.current);

    const pairedMap = new Map(pairedCounts.map((p) => [p.id, p.count]));

    const visibleLinks = (links || []).filter((l) => {
      const ids = new Set(
        outcomes
          .filter((o) => Number(o.usage_count) > 0)
          .map((o) => Number(o.id))
      );
      return ids.has(Number(l.outcome1)) && ids.has(Number(l.outcome2));
    });
    const maxLink = visibleLinks.length
      ? Math.max(...visibleLinks.map((l) => l.link_count))
      : 1;
    maxLinkRef.current = maxLink;

    // Connected nodes: outcomes appearing in at least one pair
    const connectedNodes = outcomes
      .filter((o) => (pairedMap.get(Number(o.id)) || 0) > 0)
      .map((o) => ({
        id: Number(o.id),
        outcome_id: Number(o.id),
        title: o.title,
        shortLabel: o.shortLabel || null,
        domain: o.domain,
        paired_count: pairedMap.get(Number(o.id)) || 0,
        solo_count: 0,
        is_isolated: false,
        vx: 0,
        vy: 0,
        fx: 0,
        fy: 0,
      }));

    // Isolated nodes: outcomes appearing in solo-measure activities.
    // Use negative IDs to distinguish from connected nodes in the same simulation.
    const isolatedNodes = soloCounts
      .filter((s) => s.count > 0)
      .map((s) => {
        const o = outcomes.find((o) => Number(o.id) === s.id);
        return {
          id: -s.id,
          outcome_id: s.id,
          title: o?.title || `${t('common.outcome')} ${s.id}`,
          shortLabel: o?.shortLabel || null,
          domain: o?.domain || '',
          paired_count: 0,
          solo_count: s.count,
          is_isolated: true,
          vx: 0,
          vy: 0,
          fx: 0,
          fy: 0,
        };
      });

    const nodes = [...connectedNodes, ...isolatedNodes];
    const radius = makeRadius(nodes, width, height);
    radiusRef.current = radius;

    // Initial positions.
    // The largest connected node(s) start near centre — a hint toward where
    // the radial-by-size gravity in simStep is already pulling them, so the
    // layout doesn't visibly hop there from a far-flung starting angle. When
    // the two largest are close in size, straddle them either side of centre
    // so their connecting link (if any) starts roughly central too. The rest
    // fan out on the existing circle around whatever's left of the middle.
    const bySize = [...connectedNodes].sort(
      (a, b) => b.paired_count - a.paired_count
    );
    const cx = width / 2,
      cy = height / 2;
    let ringNodes = bySize;
    if (bySize.length === 1) {
      bySize[0].x = cx;
      bySize[0].y = cy;
      ringNodes = [];
    } else if (bySize.length >= 2) {
      const [first, second] = bySize;
      const closeInSize = second.paired_count >= first.paired_count * 0.6;
      const offset = width * (closeInSize ? 0.06 : 0.03);
      first.x = cx - offset;
      first.y = cy;
      second.x = cx + offset;
      second.y = cy;
      ringNodes = bySize.slice(2);
    }
    ringNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(ringNodes.length, 1);
      n.x = cx + Math.cos(angle) * width * 0.38;
      n.y = cy + Math.sin(angle) * height * 0.35;
    });
    isolatedNodes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(isolatedNodes.length, 1);
      n.x = width * 0.8 + Math.cos(angle) * width * 0.03;
      n.y = height * 0.25 + Math.sin(angle) * height * 0.03;
    });

    nodesRef.current = nodes;
    alphaRef.current = 1.0;
    pinnedIdsRef.current = new Set();
    setRenderNodes(null);
    setSelectedLink(null);
    setSelectedNode(null);

    const step = () => {
      const drag = dragRef.current;
      const alpha = alphaRef.current;

      if (drag) {
        const n = nodesRef.current?.find((n) => n.id === drag.id);
        if (n) {
          n.x = drag.x;
          n.y = drag.y;
          n.vx = 0;
          n.vy = 0;
        }
      }

      // Keep stepping while node names are shown even after alpha has
      // otherwise decayed to a stop — simStep applies an alpha-independent
      // floor to the label-collision push in that case (see below), which
      // only takes effect if the loop is still calling it.
      if (drag || alpha > 0.001 || showNodeLabelsRef.current) {
        const effectiveAlpha = drag ? 0.35 : alpha;
        if (!drag) alphaRef.current = alpha * 0.992;
        simStep(
          nodesRef.current,
          visibleLinks,
          width,
          heightRef.current,
          radiusRef.current,
          maxLinkRef.current,
          effectiveAlpha,
          drag?.id,
          pinnedIdsRef.current,
          showNodeLabelsRef.current
        );
        setRenderNodes(nodesRef.current.map((n) => ({ ...n })));
      }

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [outcomes, links, pairedCounts, soloCounts, width]); // eslint-disable-line react-hooks/exhaustive-deps

  const svgCoords = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleNodeMouseDown = useCallback((e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    const { x, y } = svgCoords(e);
    dragRef.current = { id: nodeId, x, y, startX: x, startY: y };
    dragMovedRef.current = false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { x, y } = svgCoords(e);
    if (!dragMovedRef.current) {
      const d = Math.sqrt(
        (x - dragRef.current.startX) ** 2 + (y - dragRef.current.startY) ** 2
      );
      if (d > 5) {
        dragMovedRef.current = true;
        alphaRef.current = Math.max(alphaRef.current, 0.3);
        setIsDragging(true);
      }
    }
    dragRef.current = { ...dragRef.current, x, y };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseUp = useCallback(() => {
    if (dragRef.current) {
      const wasDrag = dragMovedRef.current;
      const draggedId = dragRef.current.id;
      dragRef.current = null;
      setIsDragging(false);
      if (wasDrag) {
        // Manually positioned — stays exactly here, excluded from all
        // physics, until the chart's underlying data reloads.
        pinnedIdsRef.current.add(draggedId);
        alphaRef.current = Math.max(alphaRef.current, 0.25);
      }
    }
  }, []);

  // Two-tap connection reveal — isolated nodes can be selected but have no links
  const handleNodeClick = useCallback(
    (e, nodeId) => {
      e.stopPropagation();
      if (dragMovedRef.current) {
        dragMovedRef.current = false;
        return;
      }
      dragMovedRef.current = false;

      // Isolated nodes (negative IDs): single-tap selects/deselects only
      if (nodeId < 0) {
        setSelectedNode((prev) => (prev === nodeId ? null : nodeId));
        setSelectedLink(null);
        return;
      }

      setSelectedNode((prev) => {
        if (prev === nodeId) {
          setSelectedLink(null);
          return null;
        }
        if (prev === null || prev < 0) {
          setSelectedLink(null);
          return nodeId;
        }

        const visibleIdSet = new Set((nodesRef.current || []).map((n) => n.id));
        const linkData = (links || []).find(
          (l) =>
            visibleIdSet.has(Number(l.outcome1)) &&
            visibleIdSet.has(Number(l.outcome2)) &&
            ((Number(l.outcome1) === prev && Number(l.outcome2) === nodeId) ||
              (Number(l.outcome2) === prev && Number(l.outcome1) === nodeId))
        );
        if (linkData) {
          setSelectedLink(`${linkData.outcome1}-${linkData.outcome2}`);
          return null;
        }
        setSelectedLink(null);
        return nodeId;
      });
    },
    [links]
  );

  const clearSelection = useCallback(() => {
    setSelectedLink(null);
    setSelectedNode(null);
  }, []);

  const visibleNodeIds = new Set(renderNodes?.map((n) => n.id) ?? []);
  const visibleLinks = (links || []).filter(
    (l) =>
      visibleNodeIds.has(Number(l.outcome1)) &&
      visibleNodeIds.has(Number(l.outcome2))
  );
  const maxLink = visibleLinks.length
    ? Math.max(...visibleLinks.map((l) => l.link_count))
    : 1;
  const radius = radiusRef.current;

  // Stats line counts: planCount is genuinely plan-level (total evaluation
  // instances, matches "Surveys conducted" exactly). dual/singleActivityCount
  // are ACTIVITY-level — every completed single activity measures either 1
  // or 2 distinct outcomes in total across all its plans combined (verified
  // against production data, zero exceptions), so activities split cleanly
  // into dual vs single. An activity that ran two separate single-outcome
  // plans still counts as one dual activity here, not two single ones — see
  // the file header for why that distinction matters. All three counts come
  // from the co-occurrences endpoint, scoped to completed (Archived)
  // activities only, same as the chart itself.
  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <div className="on__stats">
        <Trans
          i18nKey="image_picker.plan_count_summary_fmt"
          values={{
            planCount,
            evalPlans: t('image_picker.evaluation_plans', { count: planCount }),
            dualActivityCount,
            dualOutcomes: t('image_picker.dual_outcomes', {
              count: dualActivityCount,
            }),
            singleActivityCount,
            singleOutcomes: t('image_picker.single_outcomes', {
              count: singleActivityCount,
            }),
          }}
        />
      </div>
      {!renderNodes ? (
        <div
          style={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            fontSize: 13,
          }}
        >
          {t('charts.outcome_network.computing_layout')}…
        </div>
      ) : (
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{
            display: 'block',
            userSelect: 'none',
            cursor: isDragging ? 'grabbing' : 'default',
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={clearSelection}
        >
          <defs>
            {renderNodes.map((n) => (
              <clipPath key={`clip-${n.id}`} id={`clip-${n.id}`}>
                <circle cx={n.x} cy={n.y} r={radius(n)} />
              </clipPath>
            ))}
          </defs>

          {/* Lines (connected nodes only) */}
          {visibleLinks.map(({ outcome1, outcome2, link_count }) => {
            const s = renderNodes.find((n) => n.id === Number(outcome1));
            const t = renderNodes.find((n) => n.id === Number(outcome2));
            if (!s || !t) return null;
            const dx = t.x - s.x,
              dy = t.y - s.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const rs = radius(s),
              rt = radius(t);
            if (dist <= rs + rt) return null;

            const key = `${outcome1}-${outcome2}`;
            const isSelected = selectedLink === key;
            const dimmed =
              (selectedLink && !isSelected) ||
              (selectedNode !== null && !selectedLink);
            const norm = link_count / maxLink;
            const opacity = dimmed ? 0.5 : isSelected ? 1 : 0.3 + norm * 0.6;
            const sw = isSelected ? 3.5 + norm * 4.5 : 1.0 + norm * 4.5;
            const stroke = isSelected ? '#f59e0b' : '#475569';

            return (
              <line
                key={key}
                x1={s.x + (dx / dist) * rs}
                y1={s.y + (dy / dist) * rs}
                x2={t.x - (dx / dist) * rt}
                y2={t.y - (dy / dist) * rt}
                stroke={stroke}
                strokeWidth={sw}
                strokeOpacity={opacity}
                strokeLinecap="round"
              />
            );
          })}

          {/* Opaque white backing — prevents lines bleeding through nodes */}
          {renderNodes.map((n) => (
            <circle
              key={`bg-${n.id}`}
              cx={n.x}
              cy={n.y}
              r={radius(n)}
              fill="white"
            />
          ))}

          {/* Nodes — both connected and isolated */}
          {renderNodes.map((n) => {
            const r = radius(n);
            const bg = colorFor(n.domain, 0);
            const icon = ICON[n.outcome_id];

            const selLinkData = selectedLink
              ? visibleLinks.find(
                  (l) => `${l.outcome1}-${l.outcome2}` === selectedLink
                )
              : null;
            const isLinkNode =
              selLinkData &&
              (Number(selLinkData.outcome1) === n.id ||
                Number(selLinkData.outcome2) === n.id);
            const isSelected = selectedNode === n.id;

            let ringStroke = 'rgba(255,255,255,0.35)';
            let ringWidth = 1.5;
            let nodeOpacity = 1;

            if (selectedLink) {
              if (isLinkNode) {
                ringStroke = 'rgba(245,158,11,0.95)';
                ringWidth = 3;
              } else {
                nodeOpacity = 0.5;
              }
            } else if (selectedNode !== null) {
              if (isSelected) {
                ringStroke = 'rgba(245,158,11,0.95)';
                ringWidth = 3;
              } else {
                nodeOpacity = 0.7;
              }
            }

            // Count pill below isolated node
            const pillLabel = String(n.solo_count);
            const pillPW = pillLabel.length * 6 + 10;
            const pillPH = 15;

            return (
              <g
                key={n.id}
                onMouseEnter={() => !isDragging && setHoveredNode(n.id)}
                onMouseLeave={() => setHoveredNode(null)}
                onMouseDown={(e) => handleNodeMouseDown(e, n.id)}
                onClick={(e) => handleNodeClick(e, n.id)}
                style={{
                  cursor: isDragging ? 'grabbing' : 'grab',
                  opacity: nodeOpacity,
                }}
              >
                <circle cx={n.x} cy={n.y} r={r} fill={bg} />

                {icon && (
                  <image
                    href={`/icons/outcomes/${icon}.png`}
                    x={n.x - r * 0.7}
                    y={n.y - r * 0.7}
                    width={r * 1.4}
                    height={r * 1.4}
                    clipPath={`url(#clip-${n.id})`}
                    style={{ pointerEvents: 'none' }}
                  />
                )}

                <circle
                  cx={n.x}
                  cy={n.y}
                  r={r}
                  fill="none"
                  stroke={ringStroke}
                  strokeWidth={ringWidth}
                />

                {/* Count pill below isolated nodes */}
                {n.is_isolated && (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={n.x - pillPW / 2}
                      y={n.y + r + 3}
                      width={pillPW}
                      height={pillPH}
                      rx={7}
                      ry={7}
                      fill="white"
                      stroke="#cbd5e1"
                      strokeWidth={0.75}
                    />
                    <text
                      x={n.x}
                      y={n.y + r + 3 + pillPH / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={10}
                      fontWeight={700}
                      fill="#374151"
                    >
                      {pillLabel}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Always-on node name labels — "Show node names" toggle. Distinct
              from the hover/select title label below: single line (shortLabel,
              or a truncated title when no shortLabel is set), lightly tinted
              so it stays legible sitting on top of links without competing
              with them visually. Isolated nodes already have a count pill
              directly below them, so their name sits below that instead. */}
          {showNodeLabels &&
            renderNodes.map((n) => {
              const r = radius(n);
              const text = nodeLabelText(n);
              const boxW = text.length * 5.6 + 10;
              const boxH = 14;
              const boxX = Math.max(
                2,
                Math.min(width - boxW - 2, n.x - boxW / 2)
              );
              const boxY = n.is_isolated ? n.y + r + 21 : n.y + r + 4;
              return (
                <g key={`name-${n.id}`} style={{ pointerEvents: 'none' }}>
                  <rect
                    x={boxX}
                    y={boxY}
                    width={boxW}
                    height={boxH}
                    rx={3}
                    ry={3}
                    fill="#ffffff"
                    fillOpacity={0.5}
                  />
                  <text
                    x={boxX + boxW / 2}
                    y={boxY + boxH / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={10}
                    fontWeight={600}
                    fill="#1f2937"
                  >
                    {text}
                  </text>
                </g>
              );
            })}

          {/* Link count labels (connected nodes only) */}
          {visibleLinks.map(({ outcome1, outcome2, link_count }) => {
            const s = renderNodes.find((n) => n.id === Number(outcome1));
            const t = renderNodes.find((n) => n.id === Number(outcome2));
            if (!s || !t) return null;
            const dx = t.x - s.x,
              dy = t.y - s.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const rs = radius(s),
              rt = radius(t);
            if (dist <= rs + rt) return null;

            const x1 = s.x + (dx / dist) * rs,
              y1 = s.y + (dy / dist) * rs;
            const x2 = t.x - (dx / dist) * rt,
              y2 = t.y - (dy / dist) * rt;

            // Find the midpoint with the most clearance from all nodes
            const SAMPLES = 24;
            let bestX = (x1 + x2) / 2,
              bestY = (y1 + y2) / 2,
              bestClearance = -Infinity;
            for (let si = 0; si <= SAMPLES; si++) {
              const tt = si / SAMPLES;
              const px = x1 + tt * (x2 - x1),
                py = y1 + tt * (y2 - y1);
              let clearance = Infinity;
              renderNodes.forEach((node) => {
                const c =
                  Math.sqrt((px - node.x) ** 2 + (py - node.y) ** 2) -
                  radius(node);
                if (c < clearance) clearance = c;
              });
              if (clearance > bestClearance) {
                bestClearance = clearance;
                bestX = px;
                bestY = py;
              }
            }
            let mx = bestX,
              my = bestY;
            if (bestClearance < 10) {
              renderNodes.forEach((node) => {
                const ndx = mx - node.x,
                  ndy = my - node.y;
                const nd = Math.sqrt(ndx * ndx + ndy * ndy) || 1;
                const minD = radius(node) + 10;
                if (nd < minD) {
                  const p = (minD - nd) / nd;
                  mx += ndx * p;
                  my += ndy * p;
                }
              });
            }

            const key = `${outcome1}-${outcome2}`;
            const isSelected = selectedLink === key;
            const dimmed =
              (selectedLink && !isSelected) ||
              (selectedNode !== null && !selectedLink);
            const label = String(link_count);
            const fs = isSelected ? 15 : 10;
            const pw = isSelected
              ? label.length * 9 + 22
              : label.length * 6 + 10;
            const ph = isSelected ? 26 : 15;
            const rx = isSelected ? 13 : 7;

            // Clicking an already-highlighted link opens the outcome-pair
            // analysis page in a new tab; clicking an unselected link just
            // highlights it (same effect as selecting both endpoint nodes).
            const openRelationship = () => {
              const params = new URLSearchParams({
                o1: outcome1,
                o2: outcome2,
                o1name: s.title || '',
                o2name: t.title || '',
                o1domain: s.domain || '',
                o2domain: t.domain || '',
                count: link_count,
              });
              window.open(
                `/admin/report-builder/outcome-relationship?${params}`,
                '_blank'
              );
            };

            return (
              <g
                key={`lbl-${key}`}
                style={{ cursor: 'pointer', opacity: dimmed ? 0.5 : 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isSelected) {
                    openRelationship();
                    return;
                  }
                  setSelectedNode(null);
                  setSelectedLink(key);
                }}
              >
                {showLinkCounts ? (
                  <>
                    <rect
                      x={mx - pw / 2}
                      y={my - ph / 2}
                      width={pw}
                      height={ph}
                      rx={rx}
                      ry={rx}
                      fill={isSelected ? '#f59e0b' : 'white'}
                      stroke={isSelected ? '#d97706' : '#cbd5e1'}
                      strokeWidth={isSelected ? 2 : 0.75}
                    />
                    <text
                      x={mx}
                      y={my}
                      textAnchor="middle"
                      fontSize={fs}
                      fontWeight="700"
                      fill={isSelected ? 'white' : '#374151'}
                      dominantBaseline="middle"
                    >
                      {label}
                    </text>
                  </>
                ) : (
                  // Badges hidden — keep an invisible hit target at the same
                  // spot so the link is still clickable to select/open.
                  <circle
                    cx={mx}
                    cy={my}
                    r={Math.max(pw, ph) / 2}
                    fill="transparent"
                  />
                )}
              </g>
            );
          })}

          {/* Title label(s) — the hovered node, the selected node, or both
              endpoints of a selected link. Anchored to the node's own
              SVG coordinates (not the cursor), so it tracks the node even
              while the simulation is still settling it, and sits directly
              below it rather than drifting away. Title only, no count —
              counts are shown elsewhere (node tooltip removed; see stats
              line and link badges). Rendered last so it sits on top.
              Same white/50%-opacity, dark-text styling as the always-on
              "Show node names" labels for visual consistency between the
              two. Hover is skipped entirely when node names are already
              always-on — the name is already sitting right there, so a
              hover tip would just be a redundant duplicate. */}
          {(() => {
            const targetIds = new Set();
            if (hoveredNode !== null && !showNodeLabels)
              targetIds.add(hoveredNode);
            if (selectedLink) {
              const ld = visibleLinks.find(
                (l) => `${l.outcome1}-${l.outcome2}` === selectedLink
              );
              if (ld) {
                targetIds.add(Number(ld.outcome1));
                targetIds.add(Number(ld.outcome2));
              }
            } else if (selectedNode !== null) {
              targetIds.add(selectedNode);
            }
            const targets = [...targetIds]
              .map((id) => renderNodes.find((n) => n.id === id))
              .filter(Boolean);
            return targets.map((n) => {
              const r = radius(n);
              const lines = wrapTitle(n.title);
              const lineH = 13;
              const boxW = Math.max(...lines.map((l) => l.length)) * 6.2 + 16;
              const boxH = lines.length * lineH + 8;
              const boxX = Math.max(
                4,
                Math.min(width - boxW - 4, n.x - boxW / 2)
              );
              const boxY = Math.min(
                height - boxH - 4,
                n.y + r + (n.is_isolated ? 22 : 8)
              );
              return (
                <g key={`title-${n.id}`} style={{ pointerEvents: 'none' }}>
                  <rect
                    x={boxX}
                    y={boxY}
                    width={boxW}
                    height={boxH}
                    rx={5}
                    ry={5}
                    fill="#ffffff"
                    fillOpacity={0.5}
                  />
                  {lines.map((line, i) => (
                    <text
                      key={i}
                      x={boxX + boxW / 2}
                      y={boxY + 14 + i * lineH}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill="#1f2937"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            });
          })()}
        </svg>
      )}

      {selectedNode !== null && !selectedLink && selectedNode > 0 && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#6b7280',
            marginTop: 6,
            pointerEvents: 'none',
          }}
        >
          {t('charts.outcome_network.click_a_second_outcome')}
        </div>
      )}
    </div>
  );
}
